import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, RotateCcw, Maximize2, Minimize2, BarChart3, Hash, Zap, CheckCircle2, Globe, DollarSign, Percent, Calculator } from 'lucide-react';

interface FormatStats {
  formatsGenerated: number;
  mostUsedFormat: string;
  largestNumber: string;
  totalFormatTime: number;
  averageFormatTime: number;
  lastFormat: string;
}

interface FormatResult {
  originalNumber: string;
  formats: { [key: string]: string };
  locale: string;
  timestamp: Date;
  processingTime: number;
  numberInfo: {
    isInteger: boolean;
    isNegative: boolean;
    decimalPlaces: number;
    magnitude: string;
    scientificNotation: string;
  };
}

interface CurrencyOption {
  code: string;
  symbol: string;
  name: string;
}

const NumberFormatterTool: React.FC = () => {
  const [inputNumber, setInputNumber] = useState<string>('1234567.89');
  const [selectedLocale, setSelectedLocale] = useState<string>('en-US');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const [customFormat, setCustomFormat] = useState<string>('');
  const [decimalPlaces, setDecimalPlaces] = useState<number>(2);
  const [useGrouping, setUseGrouping] = useState<boolean>(true);
  const [showScientific, setShowScientific] = useState<boolean>(true);
  const [showFractions, setShowFractions] = useState<boolean>(false);
  const [showBinary, setShowBinary] = useState<boolean>(false);
  const [showPercentage, setShowPercentage] = useState<boolean>(true);
  const [result, setResult] = useState<FormatResult | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<FormatStats>({
    formatsGenerated: 0,
    mostUsedFormat: '',
    largestNumber: '0',
    totalFormatTime: 0,
    averageFormatTime: 0,
    lastFormat: ''
  });
  const [notification, setNotification] = useState<string>('');
  const [history, setHistory] = useState<FormatResult[]>([]);

  const locales = [
    { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
    { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
    { code: 'de-DE', name: 'German', flag: '🇩🇪' },
    { code: 'fr-FR', name: 'French', flag: '🇫🇷' },
    { code: 'es-ES', name: 'Spanish', flag: '🇪🇸' },
    { code: 'it-IT', name: 'Italian', flag: '🇮🇹' },
    { code: 'ja-JP', name: 'Japanese', flag: '🇯🇵' },
    { code: 'zh-CN', name: 'Chinese (Simplified)', flag: '🇨🇳' },
    { code: 'ko-KR', name: 'Korean', flag: '🇰🇷' },
    { code: 'ru-RU', name: 'Russian', flag: '🇷🇺' },
    { code: 'ar-SA', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi-IN', name: 'Hindi', flag: '🇮🇳' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)', flag: '🇧🇷' },
    { code: 'nl-NL', name: 'Dutch', flag: '🇳🇱' },
    { code: 'sv-SE', name: 'Swedish', flag: '🇸🇪' }
  ];

  const currencies: CurrencyOption[] = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' }
  ];

  const parseNumber = (input: string): number | null => {
    if (!input || input.trim() === '') return null;
    
    // Remove common formatting characters
    const cleaned = input.replace(/[,\s]/g, '');
    const parsed = parseFloat(cleaned);
    
    return isNaN(parsed) ? null : parsed;
  };

  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };

  const toFraction = (decimal: number): string => {
    if (Number.isInteger(decimal)) {
      return `${decimal}/1`;
    }

    const tolerance = 1.0E-6;
    let h1 = 1; let h2 = 0;
    let k1 = 0; let k2 = 1;
    let b = decimal;

    do {
      const a = Math.floor(b);
      let aux = h1; h1 = a * h1 + h2; h2 = aux;
      aux = k1; k1 = a * k1 + k2; k2 = aux;
      b = 1 / (b - a);
    } while (Math.abs(decimal - h1 / k1) > decimal * tolerance);

    const commonDivisor = gcd(Math.abs(h1), Math.abs(k1));
    return `${h1 / commonDivisor}/${k1 / commonDivisor}`;
  };

  const getNumberInfo = (num: number): FormatResult['numberInfo'] => {
    const isInteger = Number.isInteger(num);
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    
    let decimalPlaces = 0;
    if (!isInteger) {
      const str = num.toString();
      if (str.includes('.')) {
        decimalPlaces = str.split('.')[1].length;
      }
    }

    let magnitude = '';
    if (absNum >= 1e12) magnitude = 'Trillion';
    else if (absNum >= 1e9) magnitude = 'Billion';
    else if (absNum >= 1e6) magnitude = 'Million';
    else if (absNum >= 1e3) magnitude = 'Thousand';
    else if (absNum >= 1) magnitude = 'Unit';
    else if (absNum >= 1e-3) magnitude = 'Thousandth';
    else if (absNum >= 1e-6) magnitude = 'Millionth';
    else if (absNum >= 1e-9) magnitude = 'Billionth';
    else magnitude = 'Smaller';

    const scientificNotation = num.toExponential();

    return {
      isInteger,
      isNegative,
      decimalPlaces,
      magnitude,
      scientificNotation
    };
  };

  const formatOrdinal = (num: number, locale: string): string => {
    // Try using Intl.PluralRules for ordinal formatting
    try {
      const pr = new Intl.PluralRules(locale, { type: 'ordinal' });
      const rule = pr.select(num);
      
      // English ordinal suffixes
      const suffixes: { [key: string]: string } = {
        'one': 'st',
        'two': 'nd',
        'few': 'rd',
        'other': 'th'
      };
      
      return `${num}${suffixes[rule] || 'th'}`;
    } catch (e) {
      // Fallback for ordinal formatting
      const lastDigit = num % 10;
      const lastTwoDigits = num % 100;
      let suffix = 'th';
      
      if (lastTwoDigits < 11 || lastTwoDigits > 13) {
        if (lastDigit === 1) suffix = 'st';
        else if (lastDigit === 2) suffix = 'nd';
        else if (lastDigit === 3) suffix = 'rd';
      }
      
      return `${num}${suffix}`;
    }
  };

  const numberToWords = (num: number): string => {
    if (num === 0) return 'zero';
    
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    const thousands = ['', 'thousand', 'million', 'billion', 'trillion'];
    
    const convertHundreds = (n: number): string => {
      let result = '';
      
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' hundred';
        n %= 100;
        if (n > 0) result += ' ';
      }
      
      if (n >= 20) {
        result += tens[Math.floor(n / 10)];
        n %= 10;
        if (n > 0) result += '-' + ones[n];
      } else if (n >= 10) {
        result += teens[n - 10];
      } else if (n > 0) {
        result += ones[n];
      }
      
      return result;
    };
    
    if (num < 0) {
      return 'negative ' + numberToWords(-num);
    }
    
    if (num < 1000) {
      return convertHundreds(num);
    }
    
    let result = '';
    let thousandIndex = 0;
    
    while (num > 0) {
      const chunk = num % 1000;
      if (chunk !== 0) {
        const chunkWords = convertHundreds(chunk);
        if (thousandIndex > 0) {
          result = chunkWords + ' ' + thousands[thousandIndex] + (result ? ' ' + result : '');
        } else {
          result = chunkWords;
        }
      }
      num = Math.floor(num / 1000);
      thousandIndex++;
    }
    
    return result;
  };

  const formatNumber = useCallback((): FormatResult | null => {
    const startTime = performance.now();
    const num = parseNumber(inputNumber);
    
    if (num === null) return null;

    try {
      const formats: { [key: string]: string } = {};

      // Basic number formatting
      formats['Default'] = new Intl.NumberFormat(selectedLocale, {
        useGrouping,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      }).format(num);

      // Currency formatting
      formats['Currency'] = new Intl.NumberFormat(selectedLocale, {
        style: 'currency',
        currency: selectedCurrency,
        useGrouping
      }).format(num);

      // Percentage formatting
      if (showPercentage) {
        formats['Percentage'] = new Intl.NumberFormat(selectedLocale, {
          style: 'percent',
          minimumFractionDigits: Math.min(decimalPlaces, 2),
          maximumFractionDigits: Math.min(decimalPlaces, 2)
        }).format(num / 100);
      }

      // Scientific notation
      if (showScientific) {
        formats['Scientific'] = num.toExponential(decimalPlaces);
        formats['Engineering'] = num.toExponential().replace(/e([+-]?\d+)/, (match, exp) => {
          const exponent = parseInt(exp);
          const adjustedExp = Math.floor(exponent / 3) * 3;
          const coefficient = num / Math.pow(10, adjustedExp);
          return `${coefficient.toFixed(decimalPlaces)}e${adjustedExp >= 0 ? '+' : ''}${adjustedExp}`;
        });
      }

      // Compact notation
      try {
        formats['Compact Short'] = new Intl.NumberFormat(selectedLocale, {
          notation: 'compact',
          compactDisplay: 'short'
        }).format(num);
        
        formats['Compact Long'] = new Intl.NumberFormat(selectedLocale, {
          notation: 'compact',
          compactDisplay: 'long'
        }).format(num);
      } catch (e) {
        // Fallback for older browsers
        if (Math.abs(num) >= 1e9) {
          formats['Compact Short'] = `${(num / 1e9).toFixed(1)}B`;
        } else if (Math.abs(num) >= 1e6) {
          formats['Compact Short'] = `${(num / 1e6).toFixed(1)}M`;
        } else if (Math.abs(num) >= 1e3) {
          formats['Compact Short'] = `${(num / 1e3).toFixed(1)}K`;
        } else {
          formats['Compact Short'] = num.toString();
        }
      }

      // Binary, Octal, Hexadecimal (for integers)
      if (showBinary && Number.isInteger(num) && num >= 0 && num <= Number.MAX_SAFE_INTEGER) {
        formats['Binary'] = '0b' + num.toString(2);
        formats['Octal'] = '0o' + num.toString(8);
        formats['Hexadecimal'] = '0x' + num.toString(16).toUpperCase();
      }

      // Fraction representation
      if (showFractions && Math.abs(num) < 1000) {
        formats['Fraction'] = toFraction(num);
      }

      // Ordinal numbers (for integers) - Fixed implementation
      if (Number.isInteger(num) && num > 0 && num <= 1000) {
        formats['Ordinal'] = formatOrdinal(num, selectedLocale);
      }

      // Spell out numbers (for reasonable ranges) - Fixed implementation
      if (Math.abs(num) <= 999999 && Number.isInteger(num)) {
        formats['Spelled Out'] = numberToWords(num);
      }

      // Fixed decimal places variations
      formats['No Decimals'] = new Intl.NumberFormat(selectedLocale, {
        useGrouping,
        maximumFractionDigits: 0
      }).format(num);

      formats['Two Decimals'] = new Intl.NumberFormat(selectedLocale, {
        useGrouping,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);

      formats['Four Decimals'] = new Intl.NumberFormat(selectedLocale, {
        useGrouping,
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
      }).format(num);

      // Roman numerals (for small positive integers)
      if (Number.isInteger(num) && num > 0 && num <= 3999) {
        const romanNumerals = [
          { value: 1000, symbol: 'M' },
          { value: 900, symbol: 'CM' },
          { value: 500, symbol: 'D' },
          { value: 400, symbol: 'CD' },
          { value: 100, symbol: 'C' },
          { value: 90, symbol: 'XC' },
          { value: 50, symbol: 'L' },
          { value: 40, symbol: 'XL' },
          { value: 10, symbol: 'X' },
          { value: 9, symbol: 'IX' },
          { value: 5, symbol: 'V' },
          { value: 4, symbol: 'IV' },
          { value: 1, symbol: 'I' }
        ];

        let result = '';
        let remaining = num;

        for (const { value, symbol } of romanNumerals) {
          const count = Math.floor(remaining / value);
          if (count > 0) {
            result += symbol.repeat(count);
            remaining -= value * count;
          }
        }

        formats['Roman Numerals'] = result;
      }

      // Custom format if provided
      if (customFormat && customFormat.trim()) {
        try {
          // Simple custom format implementation
          let custom = customFormat;
          custom = custom.replace(/N/g, num.toString());
          custom = custom.replace(/\{number\}/g, formats['Default']);
          formats['Custom'] = custom;
        } catch (e) {
          formats['Custom'] = 'Invalid format';
        }
      }

      const endTime = performance.now();
      const numberInfo = getNumberInfo(num);

      return {
        originalNumber: inputNumber,
        formats,
        locale: selectedLocale,
        timestamp: new Date(),
        processingTime: endTime - startTime,
        numberInfo
      };
    } catch (error) {
      return null;
    }
  }, [inputNumber, selectedLocale, selectedCurrency, customFormat, decimalPlaces, useGrouping, showScientific, showFractions, showBinary, showPercentage]);

  const performFormatting = useCallback((): void => {
    const formatResult = formatNumber();
    if (formatResult) {
      setResult(formatResult);
      setHistory(prev => [formatResult, ...prev.slice(0, 9)]); // Keep last 10 results
      
      setStats(prev => ({
        formatsGenerated: prev.formatsGenerated + 1,
        mostUsedFormat: selectedLocale, // Simplified for this example
        largestNumber: Math.abs(parseNumber(inputNumber) || 0) > Math.abs(parseNumber(prev.largestNumber) || 0) ? inputNumber : prev.largestNumber,
        totalFormatTime: prev.totalFormatTime + formatResult.processingTime,
        averageFormatTime: (prev.totalFormatTime + formatResult.processingTime) / (prev.formatsGenerated + 1),
        lastFormat: selectedLocale
      }));
    } else {
      setResult(null);
    }
  }, [formatNumber, selectedLocale, inputNumber]);

  useEffect(() => {
    if (inputNumber.trim()) {
      performFormatting();
    }
  }, [inputNumber, selectedLocale, selectedCurrency, customFormat, decimalPlaces, useGrouping, showScientific, showFractions, showBinary, showPercentage, performFormatting]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (format?: string): Promise<void> => {
    if (!result) return;
    
    let textToCopy = '';
    
    if (format) {
      textToCopy = result.formats[format] || '';
    } else {
      textToCopy = `Number Formatting Results:\n`;
      textToCopy += `Original: ${result.originalNumber}\n`;
      textToCopy += `Locale: ${result.locale}\n\n`;
      
      Object.entries(result.formats).forEach(([name, value]) => {
        textToCopy += `${name}: ${value}\n`;
      });
      
      textToCopy += `\nNumber Info:\n`;
      textToCopy += `Type: ${result.numberInfo.isInteger ? 'Integer' : 'Decimal'}\n`;
      textToCopy += `Sign: ${result.numberInfo.isNegative ? 'Negative' : 'Positive'}\n`;
      textToCopy += `Decimal Places: ${result.numberInfo.decimalPlaces}\n`;
      textToCopy += `Magnitude: ${result.numberInfo.magnitude}\n`;
      textToCopy += `Scientific: ${result.numberInfo.scientificNotation}\n`;
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification(format ? `${format} format copied!` : 'All formats copied!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadResult = (): void => {
    if (!result) return;
    
    let content = `Number Formatting Report\n`;
    content += `Generated: ${result.timestamp.toLocaleString()}\n`;
    content += `Original Number: ${result.originalNumber}\n`;
    content += `Locale: ${result.locale}\n`;
    content += `Processing Time: ${result.processingTime.toFixed(2)}ms\n\n`;
    
    content += `Formatted Results:\n`;
    content += `${'='.repeat(50)}\n`;
    Object.entries(result.formats).forEach(([name, value]) => {
      content += `${name.padEnd(20)}: ${value}\n`;
    });
    
    content += `\nNumber Information:\n`;
    content += `${'='.repeat(50)}\n`;
    content += `Type: ${result.numberInfo.isInteger ? 'Integer' : 'Decimal'}\n`;
    content += `Sign: ${result.numberInfo.isNegative ? 'Negative' : 'Positive'}\n`;
    content += `Decimal Places: ${result.numberInfo.decimalPlaces}\n`;
    content += `Magnitude: ${result.numberInfo.magnitude}\n`;
    content += `Scientific Notation: ${result.numberInfo.scientificNotation}\n\n`;
    
    content += `Statistics:\n`;
    content += `${'='.repeat(50)}\n`;
    content += `Total Formats Generated: ${stats.formatsGenerated}\n`;
    content += `Most Used Locale: ${stats.mostUsedFormat}\n`;
    content += `Largest Number Formatted: ${stats.largestNumber}\n`;
    content += `Average Processing Time: ${stats.averageFormatTime.toFixed(2)}ms\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'number-formatting-report.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Report downloaded!');
  };

  const clearAll = (): void => {
    setInputNumber('1234567.89');
    setSelectedLocale('en-US');
    setSelectedCurrency('USD');
    setCustomFormat('');
    setDecimalPlaces(2);
    setUseGrouping(true);
    setShowScientific(true);
    setShowFractions(false);
    setShowBinary(false);
    setShowPercentage(true);
    setResult(null);
    setHistory([]);
    setStats({
      formatsGenerated: 0,
      mostUsedFormat: '',
      largestNumber: '0',
      totalFormatTime: 0,
      averageFormatTime: 0,
      lastFormat: ''
    });
    showNotification('Formatter reset!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSample = (sample: string): void => {
    setInputNumber(sample);
    showNotification('Sample loaded!');
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <Hash className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Number Formatter</h1>
              <p className="text-gray-300 text-sm">Format numbers in multiple styles, locales, and currencies</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Formatting Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.formatsGenerated}</div>
                <div className="text-gray-300 text-sm">Total Formats</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.averageFormatTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Avg Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-lg font-bold text-green-400">{stats.mostUsedFormat || 'None'}</div>
                <div className="text-gray-300 text-sm">Most Used Locale</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-lg font-bold text-yellow-400">
                  {stats.largestNumber.length > 8 ? 
                    `${stats.largestNumber.substring(0, 8)}...` : 
                    stats.largestNumber
                  }
                </div>
                <div className="text-gray-300 text-sm">Largest Number</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-lg font-bold text-teal-400">{stats.lastFormat || 'None'}</div>
                <div className="text-gray-300 text-sm">Last Locale</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input & Settings</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => loadSample('3.14159265')}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  title="Load Pi"
                >
                  <Zap className="w-4 h-4" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4 flex-1">
              {/* Number Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Number to Format
                </label>
                <input
                  type="text"
                  value={inputNumber}
                  onChange={(e) => setInputNumber(e.target.value)}
                  placeholder="Enter a number (e.g., 1234567.89)"
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Locale Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Locale
                </label>
                <select
                  value={selectedLocale}
                  onChange={(e) => setSelectedLocale(e.target.value)}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                >
                  {locales.map((locale) => (
                    <option key={locale.code} value={locale.code}>
                      {locale.flag} {locale.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Currency Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Currency
                </label>
                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                >
                  {currencies.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} {currency.name} ({currency.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Decimal Places */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Decimal Places: {decimalPlaces}
                </label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={decimalPlaces}
                  onChange={(e) => setDecimalPlaces(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* Custom Format */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Custom Format (use N for number)
                </label>
                <input
                  type="text"
                  value={customFormat}
                  onChange={(e) => setCustomFormat(e.target.value)}
                  placeholder="e.g., Value: N or #{number}"
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Options */}
              <div className="border-t border-gray-600 pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Use Grouping Separators
                  </label>
                  <button
                    onClick={() => setUseGrouping(!useGrouping)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      useGrouping ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {useGrouping ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Scientific Notation
                  </label>
                  <button
                    onClick={() => setShowScientific(!showScientific)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showScientific ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showScientific ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Percentage Format
                  </label>
                  <button
                    onClick={() => setShowPercentage(!showPercentage)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showPercentage ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showPercentage ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Fraction Format
                  </label>
                  <button
                    onClick={() => setShowFractions(!showFractions)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showFractions ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showFractions ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Binary/Hex/Octal
                  </label>
                  <button
                    onClick={() => setShowBinary(!showBinary)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showBinary ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showBinary ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>

              {/* Quick Examples */}
              <div className="border-t border-gray-600 pt-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Quick Examples
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { number: '1234567.89', label: 'Basic Decimal' },
                    { number: '0.123456', label: 'Small Decimal' },
                    { number: '1000000', label: 'One Million' },
                    { number: '42', label: 'Simple Integer' },
                    { number: '-98765.43', label: 'Negative Number' },
                    { number: '0.00001', label: 'Very Small' },
                    { number: '999999999', label: 'Large Number' }
                  ].map((example) => (
                    <button
                      key={example.number}
                      onClick={() => loadSample(example.number)}
                      className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded text-sm transition-colors text-left"
                    >
                      <div className="font-medium">{example.label}</div>
                      <div className="text-xs text-gray-400">{example.number}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Formatted Results</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => copyToClipboard()}
                  disabled={!result}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadResult}
                  disabled={!result}
                  className="p-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto min-h-[500px]">
              {result ? (
                <div className="space-y-4">
                  {/* Number Info */}
                  <div className="text-center border-b border-gray-600 pb-4">
                    <div className="text-sm text-gray-400 mb-2">
                      Original: {result.originalNumber} | Locale: {result.locale}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="bg-gray-700 p-2 rounded">
                        <div className="text-lg font-bold text-blue-400">
                          {result.numberInfo.isInteger ? 'Integer' : 'Decimal'}
                        </div>
                        <div className="text-gray-300 text-xs">Type</div>
                      </div>
                      <div className="bg-gray-700 p-2 rounded">
                        <div className="text-lg font-bold text-purple-400">
                          {result.numberInfo.isNegative ? 'Negative' : 'Positive'}
                        </div>
                        <div className="text-gray-300 text-xs">Sign</div>
                      </div>
                      <div className="bg-gray-700 p-2 rounded">
                        <div className="text-lg font-bold text-green-400">
                          {result.numberInfo.decimalPlaces}
                        </div>
                        <div className="text-gray-300 text-xs">Decimals</div>
                      </div>
                      <div className="bg-gray-700 p-2 rounded">
                        <div className="text-sm font-bold text-yellow-400">
                          {result.numberInfo.magnitude}
                        </div>
                        <div className="text-gray-300 text-xs">Magnitude</div>
                      </div>
                    </div>
                    <div className="text-gray-400 text-sm">
                      Processed in {result.processingTime.toFixed(2)}ms | {result.timestamp.toLocaleString()}
                    </div>
                  </div>

                  {/* Formatted Results */}
                  <div className="grid grid-cols-1 gap-3">
                    {Object.entries(result.formats).map(([formatName, formatValue]) => (
                      <div key={formatName} className="bg-gray-700 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-white flex items-center">
                            {formatName === 'Currency' && <DollarSign className="w-4 h-4 mr-1" />}
                            {formatName === 'Percentage' && <Percent className="w-4 h-4 mr-1" />}
                            {formatName === 'Scientific' && <Calculator className="w-4 h-4 mr-1" />}
                            {formatName}
                          </h4>
                          <button
                            onClick={() => copyToClipboard(formatName)}
                            className="p-1 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                            title={`Copy ${formatName} format`}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="bg-gray-800 p-2 rounded font-mono text-sm break-all">
                          <span className={`${
                            formatName === 'Currency' ? 'text-green-400' :
                            formatName === 'Percentage' ? 'text-blue-400' :
                            formatName === 'Scientific' ? 'text-purple-400' :
                            formatName === 'Binary' ? 'text-red-400' :
                            formatName === 'Hexadecimal' ? 'text-orange-400' :
                            formatName === 'Roman Numerals' ? 'text-yellow-400' :
                            'text-gray-300'
                          }`}>
                            {formatValue}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Hash className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Enter a number to see formatted results</p>
                    <p className="text-sm mt-2">Supports integers, decimals, and scientific notation</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Formatting</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-40 overflow-y-auto">
              {history.slice(0, 6).map((format, index) => (
                <div key={index} className="bg-gray-700 p-3 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-white">
                      {format.originalNumber}
                    </div>
                    <div className="text-xs text-gray-400">
                      {format.processingTime.toFixed(1)}ms
                    </div>
                  </div>
                  <div className="text-xs space-y-1">
                    <div className="text-blue-400">
                      Default: {format.formats['Default']}
                    </div>
                    <div className="text-green-400 truncate">
                      Currency: {format.formats['Currency']}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {format.locale} | {format.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Information Panel */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Formatting Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-blue-400 mb-2">Locale Formatting</h4>
              <p className="text-gray-300">
                Numbers are formatted according to locale-specific rules for decimal separators, 
                grouping, and cultural conventions. Each locale has unique number formatting patterns.
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-purple-400 mb-2">Currency Support</h4>
              <p className="text-gray-300">
                Automatic currency symbol placement, decimal handling, and locale-appropriate 
                formatting for 15+ major world currencies with proper positioning and spacing.
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-green-400 mb-2">Advanced Formats</h4>
              <p className="text-gray-300">
                Scientific notation, engineering notation, compact formats (K/M/B), 
                binary/hex/octal representations, fractions, ordinals, and Roman numerals.
              </p>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberFormatterTool;